import { Alert, Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import { useBranch } from "../../../../context/BranchContext";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import InputMoney from "../../../components/Forms/Element/InputMoney";
import InputDate from "../../../components/Forms/Element/InputDate";
import TextArea from "../../../components/Forms/Element/TextArea";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import FormSection from "../../../components/Common/Form/FormSection";
import { monthFromApi } from "./bonusMonth";

const Form = (props) => {

    const { t } = useTranslation();
    const { formik, entityData, resetForm } = props;
    const { selectedBranch } = useBranch();
    const editing = Boolean(entityData);

    // Filial travada quando o operador já escolheu uma no topo da tela, como no Vale. Com
    // "todas" selecionado o campo abre, porque a bonificação precisa dizer de qual filial é.
    const [disabledBranch, setDisabledBranch] = useState(false);

    useEffect(() => {
        if (editing) {
            return;
        }
        if (selectedBranch === null) {
            setDisabledBranch(false);
            formik.setFieldValue('branch', null);
        } else {
            formik.setFieldValue('branch', {
                label: selectedBranch.name,
                value: selectedBranch.id
            });
            setDisabledBranch(true);
        }
    }, [selectedBranch, resetForm]);

    useEffect(() => {
        // Preenche os campos na edição de uma bonificação já lançada.
        if (entityData) {
            formik.setFieldValue('value', entityData.value);
            formik.setFieldValue('startMonth', monthFromApi(entityData.startMonth));
            formik.setFieldValue('endMonth', monthFromApi(entityData.endMonth));
            formik.setFieldValue('description', entityData.description || '');
            formik.setFieldValue('employees', entityData.employee
                ? [{ label: entityData.employee.name, value: entityData.employee.id }]
                : []);
            formik.setFieldValue('branch', entityData.branch
                ? { label: entityData.branch.name, value: entityData.branch.id }
                : null);
        }
    }, [entityData]);

    return (
        <>
            <div className="form-layout form-layout--spaced">
                <FormSection
                    title={t('cash_flow.employee_bonus')}
                    description={t('cash_flow.employee_bonus_information_hint')}
                    icon="fa-solid fa-gift"
                >
                    {/* Na edição, colaborador e filial ficam travados: o caminho para trocar é
                        excluir e lançar de novo, e o aviso diz como mudar o valor de um mês que
                        já foi pago. */}
                    {editing && (
                        <Alert variant="info" className="py-2">
                            <i className="fa-solid fa-circle-info me-2"></i>
                            {t('cash_flow.employee_bonus_edit_hint')}
                        </Alert>
                    )}

                    {/* A filial vem antes porque filtra os colaboradores: escolher a pessoa
                        antes da unidade levaria a listar quem não trabalha nela. */}
                    <Row className="g-3">
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('branch')} <AsteriskRequired/></FormLabel>
                                <BranchSelect
                                    name="branch"
                                    isDisabled={editing || disabledBranch}
                                    value={formik.values.branch}
                                    onChange={(value) => {
                                        formik.setFieldValue('branch', value);
                                        // Trocar a filial invalida quem estava escolhido: a pessoa
                                        // pode não estar na unidade nova.
                                        formik.setFieldValue('employees', []);
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.branch}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <FormLabel>{t('cash_flow.employee_bonus_employees_label')} <AsteriskRequired/></FormLabel>
                                {/* Escolha múltipla com "Todos": uma pessoa, algumas ou a equipe
                                    toda da filial, cada uma com a sua bonificação. Sem filtro de
                                    quem atende, porque recepção e gerente também recebem. */}
                                <EmployeeSelect
                                    key={formik.values.branch?.value ?? 'all'}
                                    branch={formik.values.branch}
                                    filterProvidesService={false}
                                    isMulti={!editing}
                                    includeSelectAll={!editing}
                                    isDisabled={editing}
                                    value={editing ? formik.values.employees[0] || null : formik.values.employees}
                                    onChange={(value) => formik.setFieldValue('employees', value || [])}
                                />
                                {formik.errors.employees && (
                                    <div className="text-danger fs-12 mt-1">{formik.errors.employees}</div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row className="g-3 mt-0">
                        <Col md={4}>
                            <FormGroup>
                                <FormLabel>{t('cash_flow.employee_bonus_value')} (R$) <AsteriskRequired/></FormLabel>
                                <InputMoney
                                    name="value"
                                    value={formik.values.value}
                                    onChange={(value) => formik.setFieldValue('value', value)}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.value}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <FormLabel>{t('cash_flow.employee_bonus_start_month')} <AsteriskRequired/></FormLabel>
                                <InputDate
                                    monthOnly
                                    name="startMonth"
                                    selected={formik.values.startMonth}
                                    onChange={(value) => formik.setFieldValue('startMonth', value)}
                                    error={formik.errors.startMonth}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <FormLabel>{t('cash_flow.employee_bonus_end_month')}</FormLabel>
                                {/* Vazio = sem fim. É assim que a bonificação recorrente fica valendo
                                    até o gestor encerrar, e o bônus de uma vez só repete o mês. */}
                                <InputDate
                                    monthOnly
                                    isClearable
                                    name="endMonth"
                                    selected={formik.values.endMonth}
                                    onChange={(value) => formik.setFieldValue('endMonth', value)}
                                    error={formik.errors.endMonth}
                                />
                                <small className="text-muted d-block mt-1">
                                    {t('cash_flow.employee_bonus_end_month_hint')}
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row className="g-3 mt-0">
                        <Col md={12}>
                            <FormGroup>
                                <FormLabel>{t('description')}</FormLabel>
                                <TextArea
                                    name="description"
                                    placeholder={t('cash_flow.employee_bonus_description_placeholder')}
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    maxLength={255}
                                    style={{ height: "80px" }}
                                    counter="true"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>
            </div>
        </>
    );
}

export default Form;
